import { trim } from "utils/StringUtils";

const divEditableId = "divEditable-Formula";

class FormulaFieldFunctions {
  /** Atualiza o elementos dentro da Div Editável da fórmula transformando os textos
   * que ficam entre colchetes [] em chips.
   * @param {string} formula - Nova fórmula em texto
   * @param {object[]} validChips - Uma lista com os rótulos de chips que são válidos (usados para validar se um chip está com erro)
   */
  updateInputHTML(formula: string, validChips: { label: string }[] = []) {
    const target = document.getElementById(divEditableId);
    if (!target) return;

    // Limpa a div
    target.innerHTML = "";

    if (formula) {
      // Divide a formula separando os 'chips' dos textos
      const parts = this._splitFormula(formula);

      for (const text of parts) {
        // Se a substring for um chip, ou seja, tenha um texto entre colchetes []
        // é criado o elemento HTML para evidenciar este chip
        if (this._isTextChip(text)) {
          const textLabel = text.replace("[", "").replace("]", "");
          const hasError =
            validChips.findIndex(({ label }) => label === textLabel) === -1;

          const chip = this._createChipElement(textLabel, hasError);
          target.append(chip);
        } else {
          target.append(text);
        }
      }
    }

    this._addBlancksOnDiv(target);
    return target;
  }

  /** Obtém o texto da fórmula atual a partir dos elementos filhos da Div Editável. */
  concatCurrentFormula(): string {
    let newFormula = "";
    const target = document.getElementById(divEditableId);

    if (target) {
      const children = Array.from(target.childNodes);

      for (const node of children) {
        let text = "";
        if (this._isChip(node)) {
          text = (node as Element).getAttribute("data-value") || "";
          if (text) text = ` ${text} `;
        } else {
          text = (node as Text).data || "";
        }
        newFormula += text;
      }
    }

    return trim(newFormula);
  }

  addChipAtPosition(
    textNode: Text,
    label: string,
    position: number = 0,
    removeLength: number = 0,
    chipHasError: boolean = false
  ) {
    const target = document.getElementById(divEditableId);
    if (!target) return;

    if (this._isTextNode(textNode) && !this._isChip(textNode)) {
      let nodeToReplace = textNode.splitText(position);
      if (nodeToReplace) {
        // Se tiver texto remove o que não faz parte do chip
        if (nodeToReplace.textContent) {
          let nextTextNode = nodeToReplace.splitText(removeLength);
          // Deleta o novo node se ficar em branco
          if (!nextTextNode.textContent) nextTextNode.remove();
        }

        if (!label || nodeToReplace.textContent === "[]") {
          nodeToReplace.remove();
        } else {
          const chip = this._createChipElement(label, chipHasError);
          target.replaceChild(chip, nodeToReplace);
          this._addBlancksOnDiv(target);

          // Clica no chip para colocar o focus devolta ao campo
          setTimeout(() => (chip as any).click(), 500);
        }
      }
    }
  }

  /** Remove o chip selecionado inteiro. Esta função é acionada quando o usuário
   * aperta BACKSPACE ou DELETE com um chip selecionado.
   */
  removeChipIfSelected(isBackSpace: boolean = true): boolean {
    let selectedChip = document.querySelector(
      `#${divEditableId} > .chip.selected`
    );

    // Procura no objeto de seleção
    const { textNode, offset } = this.getSelectedNode();
    if (!selectedChip && textNode) {
      const text = textNode.textContent || "";

      // Se o elemento em foco for elemento Chip
      if (this._isChip(textNode)) {
        selectedChip = this._getChipFromTextNode(textNode) as Element;
        // Se for BACKSPACE e o elemento anterior for um elemento Chip
        // e o caret está no início do texto selecionado
      } else if (isBackSpace) {
        const previous = textNode.previousSibling;
        if (this._isChip(previous)) {
          // Verifica se o primeiro caracter é um espaço
          let isSpace = offset === 1 && text.charAt(0) === " ";
          if (offset === 0 || isSpace) selectedChip = previous as Element;
        }
        // Se não verifica se o próximo elemento é um elemento Chip
        // e o caret está no final do texto selecionado
      } else {
        const next = textNode.nextSibling;
        const lastIndex = text.length - 1;
        if (this._isChip(next)) {
          // Verifica se o último caracter é um espaço
          let isSpace = offset === lastIndex && text.charAt(lastIndex) === " ";
          if (offset === text.length || isSpace) {
            selectedChip = next as Element;
            // Adiciona espaços para não perder foco no campo
            this._addBlancksOnDiv();
          }
        }
      }
    }

    if (selectedChip) {
      // É usado o timeout para ter tempo de inserir os espaços caso precise
      setTimeout(() => selectedChip && selectedChip.remove(), 100);
      return true;
    }
    return false;
  }

  /** Percorre os filhos da Div Editável e verifica se algum textNode, que não pertence a nenhum
   * elemento Chip, tem os colchetes ou somente o colchete aberto.
   */
  findChipInText(bracketsClosed: boolean = true): Text | null {
    const target = document.getElementById(divEditableId);
    if (!target) return null;

    const children = Array.from(target.childNodes);
    if (children.length === 0) {
      target.insertAdjacentHTML("afterbegin", "&nbsp;");
      return target.firstChild as Text;
    }

    const textNode = children.find((node) => {
      if (this._isTextNode(node) && !this._isChip(node)) {
        const text = node.textContent || "";
        const indexOfOpenBracket = text.indexOf("[");
        // Se bracketsClosed for falso deve se procurar uma string de chip aberto
        const indexOfCloseBracket = bracketsClosed ? text.indexOf("]") : 0;
        return indexOfOpenBracket !== -1 && indexOfCloseBracket !== -1;
      }
      return false;
    });

    if (textNode) return textNode as Text;
    return null;
  }

  /** Verifica se o cursor de texto (caret) está sobre um chip na Div Editável. */
  focusedNodeIsChip(): boolean {
    const { textNode } = this.getSelectedNode();
    return this._isChip(textNode);
  }

  /** Move o cursor de texto (caret) da Div Editável passada por parâmetro
   * para o início do texto da fórmula.
   */
  moveCaretToHome(target: HTMLElement) {
    const selection = document.getSelection();
    if (!selection) return;

    this._addBlancksOnDiv(target);
    selection.setPosition(target, 0);
  }

  /** Move o cursor de texto (caret) para o início do chip selecionado, caso haja.
   * O que visualmente apresenta o cursor à esquerda do chip.
   */
  moveCaretToLeft() {
    const selection = document.getSelection();
    if (!selection) return;

    this._addBlancksOnDiv();

    const textNode = selection.anchorNode;
    if (textNode && this._isChip(textNode)) {
      // Adiciona o carte no começo da label do chip
      selection.setPosition(textNode, 0);
    }
  }

  /** Move o cursor de texto (caret) para o final do chip selecionado, caso haja.
   * O que visualmente apresenta o cursor à direita do chip.
   */
  moveCaretToRight() {
    const selection = document.getSelection();
    if (!selection) return;

    this._addBlancksOnDiv();

    const textNode = selection.anchorNode;
    if (textNode && this._isChip(textNode)) {
      // Adiciona o caret no final da label do chip
      const offset = (textNode as Text).data.length;
      selection.setPosition(textNode, offset);
    }
  }

  /** Move o cursor de texto (caret) da Div Editável passada por parâmetro
   * para o final do texto da fórmula.
   */
  moveCaretToEnd(target: HTMLElement) {
    const selection = document.getSelection();
    if (!selection) return;

    this._addBlancksOnDiv(target);

    // Aqui é utilizada a contagem dos elementos filhos porque o target
    // é a Div Editável, ou seja, tem textos e elementos como filhos.
    const countChildren = target.childNodes.length;
    selection.setPosition(target, countChildren - 1);
  }

  /** Adicione um observador no evento '' para verificar se o cursor de texto (caret)
   * está sobre um elemento chip. E caso esteja, faz a seleção do mesmo.
   */
  addListenerToSelectionEvent() {
    document.addEventListener("selectionchange", () => {
      const { textNode } = this.getSelectedNode();
      if (textNode && this._isTextNode(textNode)) {
        this._unselectAllChips();
        this._selectChip(textNode);
      }
    });
  }

  /** Obtém o elemento em foco na página e qual a posição
   * do cursor caso seja um textNode.
   */
  getSelectedNode(): { textNode: Node | null; offset: number } {
    const selection = document.getSelection();
    if (selection) {
      const textNode = selection.anchorNode;
      const offset = selection.anchorOffset;
      // Se o elemento em foco for a própria div retorna o filho
      if (textNode && (textNode as Element).id === divEditableId) {
        const child = textNode.childNodes.item(offset);
        return { textNode: child, offset: 0 };
      }
      return { textNode, offset };
    }
    return { textNode: null, offset: -1 };
  }

  private _createChipElement(
    label: string,
    hasError: boolean = false
  ): Element {
    // Cria div principal
    const chip = document.createElement("div");
    chip.className = hasError ? "chip error" : "chip";
    chip.setAttribute("data-value", `[${label}]`);

    // Cria span com a label
    const span = document.createElement("span");
    span.className = "chip-label";
    span.innerText = label;
    chip.append(span);

    // Cria o ícone de deletar chip
    chip.insertAdjacentHTML(
      "beforeend",
      '<svg class="chip-icon" xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 0 24 24" width="24px" fill="#000000"><path d="M0 0h24v24H0z" fill="none"/><path d="M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z"/></svg>'
    );

    // Adiciona evento de remover chip
    const icon = chip.lastElementChild;
    if (icon && icon.tagName.toLowerCase() === "svg") {
      icon.addEventListener("click", (ev) => this._removeChipElement(ev));
    }

    return chip;
  }

  /** Remove da Div Editável o chip que dispara o evento. */
  private _removeChipElement(ev) {
    const target = ev.target as Element;
    let parent = target.parentElement;

    // Verifica se o target nao era o path do svg
    if (parent && parent.classList.contains("chip-icon"))
      parent = parent.parentElement;

    if (parent && parent.classList.contains("chip")) {
      parent.remove();
    }
  }

  /** Seleciona o chip da Div Editável. */
  private _selectChip(textNode: Node): Node | null {
    const chip = this._getChipFromTextNode(textNode);
    if (chip) {
      (chip as Element).classList.add("selected");
      return chip;
    }
    return null;
  }

  /** Deseleciona todos os chips da Div Editável removendo a classe 'selected'. */
  private _unselectAllChips() {
    const selectedChips = document.querySelectorAll(
      `#${divEditableId} > .chip.selected`
    );
    selectedChips.forEach((el) => {
      if (el) el.classList.remove("selected");
    });
  }

  private _isTextChip(text) {
    return text.includes("[") && text.includes("]");
  }

  /** Valida se o elemento DOM passado por parâmetro pertence a um chip
   * verificando se a classe do elemento é 'chip' ou possui o prefixo 'chip'.
   */
  private _isChip(node?: Node | null): boolean {
    if (!node) return false;

    if (this._isTextNode(node)) {
      node = node.parentNode as Node;
    }

    return Boolean(node && String((node as any).className).includes("chip"));
  }

  private _isTextNode(node?: Node | null) {
    return Boolean(node && node.nodeType === Node.TEXT_NODE);
  }

  private _getChipFromTextNode(textNode: Node): Node | null {
    // Se não for chip retorna null
    if (!this._isChip(textNode)) return null;
    // Se o node passado por parâmetro for o chip retorna o mesmo
    if (
      (textNode as Element).classList &&
      (textNode as Element).classList.contains("chip")
    )
      return textNode;

    // Se não pega o elemento chip
    const span = textNode.parentNode as Node;
    const chip = span && span.parentNode;
    return chip ? (chip as Node) : null;
  }

  /** Divide a string da fórmula separando as substrings de chips
   * das outras partes da fórmula.
   */
  private _splitFormula(formula: string) {
    let chip: RegExpExecArray | null = null;
    const parts: string[] = [];

    do {
      const chipRegex = /(\[(.*?)\])+/g;
      chip = chipRegex.exec(formula);

      if (chip) {
        parts.push(formula.slice(0, chip.index));
        parts.push(chip[0]);

        formula = formula.slice(chip.index + chip[1].length);
      } else {
        parts.push(formula);
      }
    } while (chip !== null);

    return parts.filter((text) => text !== "");
  }

  /** Adiciona espaços dentro da div para permitir que o usuário possa digitar mesmo
   * que a fórmula comece e/ou termine com um chip.
   */
  private _addBlancksOnDiv(target?: HTMLElement | null) {
    target = target || document.getElementById(divEditableId);
    if (!target) return;

    // Caso o primeiro item da Div Editável seja um chip
    // É criado um espaço na frente para que o usuário possa digitar.
    if (this._isChip(target.firstChild)) {
      target.insertAdjacentHTML("afterbegin", "&nbsp;");
    } else if (target.firstChild && this._isTextNode(target.firstChild)) {
      // Caso seja um textNode vazio, ele subistitui pelo que permite a digitação no campo
      if (!target.firstChild.textContent) {
        target.firstChild.remove();
        target.insertAdjacentHTML("afterbegin", "&nbsp;");
      }
    }

    // Caso o último item da Div Editável seja um chip
    // É criado um espaço na final para que o usuário possa digitar.
    if (this._isChip(target.lastChild)) {
      target.insertAdjacentHTML("beforeend", "&nbsp;");
    }
  }
}

export default new FormulaFieldFunctions();



// WEBPACK FOOTER //
// ./src/common/components/FormulaField/FormulaFieldFunctions.ts